<template>
  <div class="max-w-[49rem] mx-auto mt-2">
    <div class="bg-primary-50 dark:bg-primary-900 rounded-sm border-2 border-primary-400 dark:border-primary-600 shadow-lg">
      <div class="flex items-center gap-2 px-2 py-1.5 bg-primary-50 dark:bg-primary-900">
        <div class="w-6 h-6 bg-primary rounded flex items-center justify-center shadow-md">
          <UIcon name="i-lucide-clipboard-check" class="w-4 h-4 text-white"/>
        </div>
        <h2 class="text-sm font-semibold text-orange-900 dark:text-orange-100 flex-1">
          {{ cardMessage }}
        </h2>
      </div>
      
      <form @submit.prevent="handleSubmit" class="flex flex-col gap-0.5 items-center justify-center px-2 py-2">
        <slot name="inputs"></slot>
        
        <div v-if="!showRejectReasonForm" class="flex justify-center gap-2">
          <UButton
            v-if="showApproveButton"
            @click="approveStep"
            color="primary"
            :disabled="actionLoading || approveDisabled"
            :loading="actionLoading"
            class="h-8 text-sm dark:text-white cursor-pointer"
          >
            <UIcon name="i-heroicons-check" class="w-4 h-4 mr-0.5"/>
            {{ t('processStatus.actions.approve') }}
          </UButton>
          <UButton
            v-if="showRejectButton"
            @click="onRejectClick"
            color="neutral"
            variant="subtle"
            :disabled="actionLoading"
            :loading="actionLoading"
            class="h-8 text-sm dark:text-white cursor-pointer"
          >
            <UIcon name="i-heroicons-x-mark" class="w-4 h-4 mr-0.5"/>
            {{ t('processStatus.actions.reject') }}
          </UButton>
          <UButton
            v-if="showSubmitButton"
            type="submit"
            color="primary"
            :disabled="submitDisabled"
            :loading="actionLoading"
            class="h-8 text-sm dark:text-white cursor-pointer"
          >
            <UIcon name="i-heroicons-check" class="w-4 h-4 mr-0.5"/>
            {{ t('processStatus.actions.submit') }}
          </UButton>
          <UButton
            v-if="showSaveButton"
            type="submit"
            color="primary"
            :disabled="saveDisabled"
            :loading="actionLoading"
            class="h-8 text-sm dark:text-white cursor-pointer"
          >
            <UIcon name="i-heroicons-check" class="w-4 h-4 mr-0.5"/>
            {{ t('processStatus.actions.save') }}
          </UButton>
        </div>

        <div v-if="showRejectReasonForm" class="w-full max-w-[42rem] flex flex-col gap-2">
          <label class="text-xs font-medium text-gray-700 dark:text-gray-300">{{ t('processStatus.rejectReasonLabel') }}</label>
          
          <div>
            <UTextarea
              v-model="rejectReason"
              color="neutral"
              highlight
              :placeholder="t('processStatus.rejectReasonPlaceholder')"
              size="sm"
              class="w-full"
              :rows="2"
              :maxlength="200"
              :ui="{
                base: 'text-sm resize-none h-13'
              }"
            />
            <div class="text-xs text-muted tabular-nums text-right mt-1">
              {{ rejectReason.length }}/{{ 200 }}
            </div>
          </div>
          
          <div class="flex justify-end gap-2">
            <UButton
              color="neutral"
              variant="subtle"
              class="h-8 text-sm"
              @click="cancelRejectReason"
            >
              {{ t('workflowDetail.actions.cancel') }}
            </UButton>
            <UButton
              color="primary"
              class="h-8 text-sm"
              :disabled="actionLoading || !rejectReason.trim()"
              :loading="actionLoading"
              @click="confirmRejectWithReason"
            >
              {{ t('processStatus.actions.reject') }}
            </UButton>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, resolveComponent } from 'vue'
import { useI18n } from "vue-i18n";

const UButton = resolveComponent('UButton')
const UIcon = resolveComponent('UIcon')

const { t } = useI18n()

interface Props {
  cardMessage: string;
  actionLoading: boolean;
  showApproveButton?: boolean;
  showRejectButton?: boolean;
  showSubmitButton?: boolean;
  showSaveButton?: boolean;
  approveDisabled?: boolean;
  submitDisabled?: boolean;
  saveDisabled?: boolean;
  requireRejectReason?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showApproveButton: false,
  showRejectButton: false,
  showSubmitButton: false,
  showSaveButton: false,
  approveDisabled: false,
  submitDisabled: false,
  saveDisabled: false,
  requireRejectReason: false,
});

const emit = defineEmits<{
  (e: 'approve'): void;
  (e: 'reject', payload?: { reason?: string }): void;
  (e: 'submit'): void;
}>();

const showRejectReasonForm = ref(false);
const rejectReason = ref('');

const approveStep = () => {
  emit('approve');
};

const onRejectClick = () => {
  if (props.requireRejectReason) {
    showRejectReasonForm.value = true;
  } else {
    emit('reject');
  }
};

const cancelRejectReason = () => {
  showRejectReasonForm.value = false;
  rejectReason.value = '';
};

const confirmRejectWithReason = () => {
  if (props.requireRejectReason && !rejectReason.value.trim()) {
    return;
  }
  
  emit('reject', { reason: rejectReason.value.trim() || undefined });
  cancelRejectReason();
};

const handleSubmit = () => {
  emit('submit');
};
</script>
